/* eslint-disable max-classes-per-file */
import { groupBy, head, sumBy } from "lodash";
import moment from "moment";
import { DateUtils } from "../utils/date-utils";
import { IAggregatPeriod } from "./aggregatperiod";
import { BufferedArraySegment } from "./arraysegment";
import { IDagKvantitet } from "./dagkvantitet";
import { ForsorjningskallaAggregat, IForsorjningsrad, IForsorjningsradEx } from "./forsorjningskalla";

export interface ILeveransrad {
    datum: Date;
    kvantitet: number;
    avtaladDagKvantitet: number;
    transporteratKvantitet: number;
    endastVisning: boolean;
    forsorjningsrader: IForsorjningsrad[];
}

export class LeveransraderAggregat {

    leveransrader: BufferedArraySegment<ILeveransrad>;
    forsorjningsrader: BufferedArraySegment<IForsorjningsradEx>;

    constructor(private readonly raw: ILeveransrad[]) {
        // Preparse-raw- Ser till att leveransradernas datum är ett Date (inte en string)
        if (raw && raw[0] && !(raw[0].datum instanceof Date)) {
            raw.forEach(leveransrad => leveransrad.datum = new Date(leveransrad.datum));
        }

        this.initForsorjningsrader();
        this.initLeveransrader();
    }

    initForsorjningsrader() {
        const rawForsorjningsrader = this.raw
            .reduce((agg, item) => {
                const forsorjningsrader = (item.forsorjningsrader || []) as IForsorjningsradEx[];
                forsorjningsrader.forEach(fr => fr.leveransradDatum = new Date(item.datum));
                agg.push(...forsorjningsrader);
                return agg;
            }, new Array<IForsorjningsradEx>());

        this.forsorjningsrader = new BufferedArraySegment(rawForsorjningsrader, x => x.leveransradDatum);
    }

    initLeveransrader() {
        this.leveransrader = new BufferedArraySegment(this.raw, x => x.datum);
    }

    sumForsorjningsrad(period: IAggregatPeriod) {
        return this.forsorjningsrader
            .periodSegment(period)
            .reduce((total, next) => total + next.dagkvantitet, 0);
    }

    sumLeveransrad = (period: IAggregatPeriod): number => this.leveransrader
        .periodSegment(period)
        .reduce((total, next) => total + next.kvantitet, 0);

    sumTransporteratLeveransrad = (period: IAggregatPeriod): number => Math.round(sumBy(this.leveransrader.periodSegment(period), x => x.transporteratKvantitet));

    sumAvtalatLeveransrad = (period: IAggregatPeriod): number => this.leveransrader.periodSegment(period).reduce((total, next) => total + next.avtaladDagKvantitet, 0);

    sumTransporterat = (period: IAggregatPeriod, forsorjningskallorIdn?: number[]): number =>
        // NOTE. Summa inmätt för *alla* leveransrader
        // - oavsett försörjningskälla, om inte försörjningsIdn explicit medskickats.
        Math.round(this.forsorjningsrader
            .periodSegment(period)
            .filter(forsorjningsRad => !forsorjningskallorIdn || forsorjningskallorIdn.some(kallaId => kallaId === forsorjningsRad.forsorjningskallaId))
            .reduce((agg, item) => agg + item.transporteratKvantitet, 0));

    toForsorjningsRader = (): IForsorjningsradEx[] => this.forsorjningsrader.all;

    updateAvtaladKvantitet(fordeladKvantitet: IDagKvantitet[]): void {
        const leveransraderPerDatum = groupBy(this.raw, x => moment(x.datum).format(DateUtils.dateformat));

        fordeladKvantitet.forEach(dagkvantitet => {
            const datum = moment(dagkvantitet.datum);

            let leveransrad = head(leveransraderPerDatum[datum.format(DateUtils.dateformat)]);
            if (!leveransrad) {
                leveransrad = new DefaultLeveransrad(dagkvantitet.datum);
                leveransrad.kvantitet = 0;
                this.raw.push(leveransrad);
            }

            leveransrad.avtaladDagKvantitet = dagkvantitet.kvantitet;
        });

        this.initLeveransrader();
    }

    updateLeveransradKvantitet(fordeladKvantitet: IDagKvantitet[]): void {
        const leveransraderPerDatum = groupBy(this.raw, x => moment(x.datum).format(DateUtils.dateformat));

        fordeladKvantitet.forEach(dagkvantitet => {
            const datum = moment(dagkvantitet.datum);

            let leveransrad = head(leveransraderPerDatum[datum.format(DateUtils.dateformat)]);
            if (!leveransrad) {
                leveransrad = new DefaultLeveransrad(dagkvantitet.datum);
                leveransrad.avtaladDagKvantitet = 0;
                this.raw.push(leveransrad);
            }

            leveransrad.kvantitet = dagkvantitet.kvantitet;
        });

        this.initLeveransrader();
    }

    removeForsorjningskalla(forsorjningskalla: ForsorjningskallaAggregat): void {
        this.raw.forEach(leveransrad => {
            leveransrad.forsorjningsrader = leveransrad.forsorjningsrader
                .filter(x => !(x.forsorjningskallaId === forsorjningskalla.id && x.typ === forsorjningskalla.typ));
        });

        this.initForsorjningsrader();
        this.initLeveransrader();
    }
}

class DefaultLeveransrad implements ILeveransrad {
    datum: Date;
    kvantitet: number;
    transporteratKvantitet: number;
    endastVisning: boolean;
    forsorjningsrader: IForsorjningsrad[];
    avtaladDagKvantitet: number;

    constructor(datum: Date) {
        this.datum = new Date(datum);
    }
}
